import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X as XIcon, PencilSimple, Check, NavigationArrow, MapPinArea, UserCircle, FlagCheckered } from "@phosphor-icons/react";

// Status step button rendered inside the placard so the driver can update
// status WITHOUT closing the placard. Identical visual language to the
// inline buttons in `ActiveCard` so the driver doesn't have to relearn anything.
const STATUS_STEPS = {
  er:  { short: "ER",  label: "En Route",         color: "bg-amber-500 text-black hover:bg-amber-400",   icon: NavigationArrow },
  sp:  { short: "SP",  label: "Sur Place",        color: "bg-sky-500 text-white hover:bg-sky-400",       icon: MapPinArea },
  pec: { short: "PEC", label: "Prise en Charge",  color: "bg-orange-500 text-white hover:bg-orange-400", icon: UserCircle },
  dep: { short: "DEP", label: "Déposé",           color: "bg-emerald-600 text-white hover:bg-emerald-500", icon: FlagCheckered },
};
function nextStatusOf(current) {
  const order = ["assigned", "er", "sp", "pec", "dep"];
  const idx = order.indexOf(current);
  return idx >= 0 && idx < order.length - 1 ? order[idx + 1] : null;
}

/**
 * Fullscreen "client placard" the driver shows at pickup.
 *
 * Persistence rules (fixes the long-standing bug where the placard would
 * appear to "reset" after a status update):
 *  - Closing is EXPLICIT only — the ✕ button or the Escape key.
 *    No more accidental tap-anywhere-to-close.
 *  - Status updates are exposed *inside* the placard so the driver no longer
 *    has to dismiss it to mark ER / SP / PEC / DEP.
 *  - The component reads the *latest* ride object from `rides` via `rideId`,
 *    so async background refreshes (status_history pushes, payment toggles…)
 *    don't unmount/remount it.
 */
export default function ClientNameDisplay({ rideId, rides = [], onClose, onUpdateStatus, statusLoading = false }) {
  // Resolve the freshest ride snapshot for this id. If the ride disappears
  // (deleted, archived…), fall back to nothing — parent will close us.
  const ride = rides.find((r) => r.id === rideId) || null;

  const [name, setName] = useState("");
  const [editing, setEditing] = useState(false);
  const [fontSize, setFontSize] = useState(80);
  const containerRef = useRef(null);
  const textRef = useRef(null);

  // Initial name extraction — runs once per ride id, never on every status tick.
  useEffect(() => {
    if (!ride) return;
    // Personal rides carry a clean `client_name` directly; S.DRIVE rides only
    // have an unstructured `info_block` text we need to parse.
    if (!ride.info_block && ride.client_name) {
      setName(String(ride.client_name).trim());
      return;
    }
    const lines = (ride.info_block || "").split("\n");
    let found = "";
    for (const line of lines) {
      const l = line.toLowerCase().trim();
      if (l.match(/^(client|passenger|passager|nom|name|mr|mme|mlle|m\.)\s*[:：\-\s]/i)) {
        found = line.replace(/^(client|passenger|passager|nom|name|mr|mme|mlle|m\.)\s*[:：\-]\s*/i, "").trim();
        break;
      }
    }
    if (!found) found = lines.find((l) => l.trim().length > 2)?.trim() || ride.client_name || "";
    setName(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rideId]);

  const calcFontSize = useCallback(() => {
    if (!containerRef.current || !name) return;
    const cw = containerRef.current.clientWidth * 0.88;
    const ch = containerRef.current.clientHeight * 0.5; // leave room for status bar
    let lo = 20, hi = 500, best = 40;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      ctx.font = `bold ${mid}px sans-serif`;
      const measured = ctx.measureText(name).width;
      const fits = measured < cw && mid * 1.1 < ch;
      if (fits) { best = mid; lo = mid + 1; }
      else { hi = mid - 1; }
    }
    setFontSize(best);
  }, [name]);

  useEffect(() => {
    calcFontSize();
    const onResize = () => calcFontSize();
    const onOrient = () => setTimeout(calcFontSize, 200);
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onOrient);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onOrient);
    };
  }, [calcFontSize]);

  // Escape key as an explicit close — discoverable for keyboard / Bluetooth users.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Screen Wake Lock — keep the phone awake while the placard is up so the
  // driver can hold their phone screen-out at a busy airport pickup without
  // the screen dimming/locking every 30-60s. The browser auto-releases the
  // lock when the tab loses visibility (e.g. when locked manually) — we
  // re-acquire it on `visibilitychange` so coming back to the tab restores
  // the keep-awake behaviour. Silent no-op on browsers that don't support
  // the API (older iOS Safari, etc.).
  const [wakeActive, setWakeActive] = useState(false);
  useEffect(() => {
    if (typeof navigator === "undefined" || !("wakeLock" in navigator)) return;
    let sentinel = null;
    let cancelled = false;

    const acquire = async () => {
      try {
        sentinel = await navigator.wakeLock.request("screen");
        if (cancelled) { try { await sentinel.release(); } catch {} return; }
        setWakeActive(true);
        sentinel.addEventListener("release", () => setWakeActive(false));
      } catch {
        // permission denied / battery saver / page not visible — ignore.
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible" && (!sentinel || sentinel.released)) {
        acquire();
      }
    };

    acquire();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      if (sentinel) { sentinel.release().catch(() => {}); }
      setWakeActive(false);
    };
  }, []);

  if (!ride) return null;

  const next = nextStatusOf(ride.status);
  const nextInfo = next ? STATUS_STEPS[next] : null;
  const NextIcon = nextInfo?.icon;

  return (
    <div
      ref={containerRef}
      data-testid="client-placard"
      data-ride-id={ride.id}
      data-ride-status={ride.status}
      className="fixed inset-0 z-50 bg-black flex flex-col"
    >
      {/* Top bar — only the close button is interactive here. Tapping the
          black margin around it does NOT close the placard anymore. */}
      <div className="flex items-center justify-between px-3 pt-3 shrink-0 gap-2">
        <span data-testid="placard-status" className="text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded bg-zinc-900 text-zinc-400 border border-zinc-800">
          {(ride.status || "").toUpperCase() || "—"}
        </span>
        {wakeActive && (
          <span data-testid="placard-wake-indicator" className="text-[10px] uppercase tracking-wider px-2 py-1 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30 inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            Écran maintenu
          </span>
        )}
        <Button
          data-testid="placard-close"
          variant="ghost"
          onClick={onClose}
          className="text-zinc-500 hover:text-white h-10 w-10 p-0 ml-auto"
          aria-label="Fermer le placard"
        >
          <XIcon size={28} />
        </Button>
      </div>

      {/* Name area — fills the remaining space. NO onClick here so the driver
          can tap freely without accidentally dismissing the placard. */}
      <div className="flex-1 flex items-center justify-center w-full px-4 min-h-0">
        {editing ? (
          <div className="flex items-center gap-3 px-2 w-full max-w-3xl">
            <Input
              data-testid="edit-client-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              className="bg-transparent border-b-2 border-zinc-700 border-t-0 border-l-0 border-r-0 rounded-none text-white text-center h-auto py-3 focus:border-amber-500"
              style={{ fontSize: Math.min(fontSize, 80) + "px" }}
            />
            <Button onClick={() => setEditing(false)} className="bg-amber-500 text-black h-12 w-12 p-0 shrink-0"><Check size={24} /></Button>
          </div>
        ) : (
          <div className="text-center w-full">
            <p
              ref={textRef}
              data-testid="client-name-display"
              className="text-white font-bold leading-none select-none transition-[font-size] duration-200"
              style={{ fontSize: fontSize + "px", letterSpacing: "-0.02em" }}
            >
              {name || "—"}
            </p>
            <button
              data-testid="placard-edit-name"
              onClick={(e) => { e.stopPropagation(); setEditing(true); }}
              className="mt-6 text-zinc-700 hover:text-zinc-400 transition-colors inline-flex items-center gap-1 text-xs uppercase tracking-wider"
            >
              <PencilSimple size={12} /> Modifier le nom
            </button>
          </div>
        )}
      </div>

      {/* Status update bar — driver can advance status WITHOUT closing the
          placard. Visible only while there is a meaningful next step. */}
      {nextInfo && onUpdateStatus && (
        <div className="px-4 pb-5 pt-3 shrink-0 border-t border-zinc-900 bg-black">
          <Button
            data-testid={`placard-status-${next}`}
            onClick={() => onUpdateStatus(ride.id, next)}
            disabled={statusLoading}
            className={`w-full font-semibold h-14 text-base active:scale-[0.97] ${nextInfo.color}`}
          >
            {NextIcon && <NextIcon size={22} weight="fill" className="mr-2" />}
            {nextInfo.short} — {nextInfo.label}
          </Button>
        </div>
      )}
    </div>
  );
}
